import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  effect,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroDocumentText, heroMagnifyingGlass, heroXMark } from '@ng-icons/heroicons/outline';
import type { ProviderMessageTemplate } from '../../../../core/api/generated/models/ProviderMessageTemplate';
import {
  UiButton,
  UiDialog,
  UiDialogOverlay,
  UiDialogTitle,
  UiEmptyState,
  UiFilterSearch,
  UiIconButton,
  UiInput,
  UiLabel,
  UiOptionRow,
  UiOptionRowCopy,
  UiOptionRowIcon,
} from '../../../../shared/ui';

export interface TemplatePickerSubmit {
  templateId: string;
  templateLanguage: string;
  templateComponents: Array<Record<string, unknown>>;
  contentText: string;
}

interface TemplateComponent {
  type?: string;
  text?: string;
  parameters?: Array<{ type?: string; text?: string }>;
}

/** A static text run or an editable variable slot, in body order. */
type TemplateSegment = { kind: 'text'; text: string } | { kind: 'var'; n: string };

/**
 * Two-way binds the plain text of a `contenteditable` host. Unlike `<input>`, this lets a
 * variable flow and wrap inline with the surrounding template text. Writes the host only when
 * the bound value actually differs from the live text, so typing never resets the caret while
 * repeated `{{n}}` occurrences still stay in sync.
 */
@Directive({
  selector: '[appEditableText]',
  standalone: true,
})
export class EditableTextDirective {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly value = input<string>('');
  readonly valueChange = output<string>();

  constructor() {
    effect(() => {
      const next = this.value();
      if (this.el.nativeElement.textContent !== next) {
        this.el.nativeElement.textContent = next;
      }
    });
  }

  @HostListener('input')
  protected onInput(): void {
    this.valueChange.emit(this.el.nativeElement.textContent ?? '');
  }
}

/**
 * Modal that lists approved provider templates for the current channel and lets the operator
 * pick one, fill in its body variables (`{{n}}`), and dispatch it. Provider-agnostic — the
 * `components` shape parsed here mirrors the canonical WhatsApp template format the API
 * surfaces, which any provider with templates is expected to align with.
 */
@Component({
  selector: 'app-template-picker-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NgIcon,
    UiButton,
    UiDialog,
    UiDialogOverlay,
    UiDialogTitle,
    UiEmptyState,
    UiFilterSearch,
    UiIconButton,
    UiInput,
    UiLabel,
    UiOptionRow,
    UiOptionRowCopy,
    UiOptionRowIcon,
    EditableTextDirective,
  ],
  providers: [provideIcons({ heroDocumentText, heroMagnifyingGlass, heroXMark })],
  template: `
    <div appDialogOverlay>
      <section appDialog class="template-picker" aria-labelledby="template-picker-title" [ngpDialogModal]="true">
        <header class="head">
          <h2 appDialogTitle id="template-picker-title">Enviar template</h2>
          <button type="button" appUiIconButton variant="ghost" size="sm" aria-label="Fechar" (click)="onClose()">
            <ng-icon name="heroXMark" size="16" />
          </button>
        </header>

        @if (!selected()) {
          <label appUiFilterSearch class="search">
            <span class="sr-only">Buscar template</span>
            <ng-icon
              name="heroMagnifyingGlass"
              size="14"
              aria-hidden="true"
              class="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-muted"
            />
            <input
              appInput
              leadingIcon
              type="search"
              aria-label="Buscar template"
              placeholder="Buscar template…"
              [ngModel]="search()"
              (ngModelChange)="search.set($event)"
            />
          </label>

          <ul class="list">
            @for (t of filtered(); track t.id) {
              <li>
                <button type="button" appUiOptionRow tall data-template-row (click)="pick(t)">
                  <span appUiOptionRowIcon>
                    <ng-icon name="heroDocumentText" size="16" aria-hidden="true" />
                  </span>
                  <span appUiOptionRowCopy>
                    <strong>{{ t.name }}</strong>
                    <small>{{ t.category }} Â· {{ t.language }}</small>
                    <small>{{ bodyOf(t) }}</small>
                  </span>
                </button>
              </li>
            } @empty {
              <li>
                <app-ui-empty-state
                  compact
                  icon="heroDocumentText"
                  title="Nenhum template aprovado disponível"
                  description="Crie ou aguarde a aprovação na aba Integração."
                >
                  <button actions type="button" appButton variant="primary" (click)="goToIntegrations()">
                    Gerenciar templates
                  </button>
                </app-ui-empty-state>
              </li>
            }
          </ul>
        } @else {
          <div class="selected">
            <p class="selected-name">{{ selected()!.name }} · {{ selected()!.language }}</p>
            <p appUiLabel class="mt-1 uppercase">Pré-visualização</p>
            <p class="preview-body">
              @for (seg of segments(); track $index) {
                @if (seg.kind === 'text') {
                  <span>{{ seg.text }}</span>
                } @else {
                  <span
                    class="var-inline"
                    role="textbox"
                    contenteditable="true"
                    [attr.aria-label]="'Variável ' + seg.n"
                    appEditableText
                    [value]="varValues()[seg.n]"
                    (valueChange)="setVar(seg.n, $event)"
                    (keydown.enter)="$event.preventDefault()"
                  ></span>
                }
              }
            </p>
          </div>

          <footer class="actions">
            <button type="button" appButton variant="ghost" (click)="selected.set(null)">Voltar</button>
            <button type="button" appButton variant="primary" [disabled]="!canSend()" (click)="onSubmit()">
              Enviar template
            </button>
          </footer>
        }
      </section>
    </div>
  `,
  styles: [`
    .template-picker { width: min(36rem, 90vw); max-height: 80vh; display: flex; flex-direction: column; }
    .head { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.2rem 0.6rem; }
    .search { margin: 0 1.2rem 0.4rem; max-width: none; }
    .list { list-style: none; padding: 0 0.4rem 1rem; margin: 0; overflow-y: auto; flex: 1; }
    .selected { padding: 0 1.2rem 0.6rem; display: flex; flex-direction: column; gap: 0.6rem; overflow-y: auto; }
    .selected-name { font-weight: 600; color: var(--color-ink); margin: 0; }
    .preview-body { background: var(--color-soft); padding: 0.7rem 0.9rem; border-radius: var(--radius-sm); white-space: pre-wrap; overflow-wrap: anywhere; font-size: 0.875rem; color: var(--color-ink); margin: 0; line-height: 1.8; }
    .var-inline { display: inline; color: var(--color-ink); border-bottom: 1px dashed var(--color-muted); padding: 0 0.15rem; outline: none; cursor: text; }
    .var-inline:not(:empty) { border-bottom-style: solid; border-bottom-color: var(--color-accent); }
    .var-inline:empty::after { content: '\\00a0\\00a0\\00a0\\00a0'; }
    .var-inline:focus { border-bottom-style: solid; border-bottom-color: var(--color-accent); }
    .actions { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.6rem 1.2rem 1rem; }
  `],
})
export class TemplatePickerDialogComponent {
  private readonly router = inject(Router);

  readonly templates = input.required<readonly ProviderMessageTemplate[]>();

  readonly submitted = output<TemplatePickerSubmit>();
  readonly dismissed = output<void>();

  protected goToIntegrations(): void {
    this.router.navigateByUrl('/integracoes');
    this.dismissed.emit();
  }

  protected readonly search = signal('');
  protected readonly selected = signal<ProviderMessageTemplate | null>(null);
  protected readonly varValues = signal<Record<string, string>>({});

  protected readonly filtered = computed(() => {
    const query = this.search().trim().toLowerCase();
    const all = this.templates();
    return query ? all.filter((t) => t.name.toLowerCase().includes(query)) : all;
  });

  protected readonly placeholders = computed<readonly string[]>(() => {
    const body = this.bodyTextOf(this.selected());
    if (!body) return [];
    const matches = new Set<string>();
    for (const m of body.matchAll(/\{\{(\d+)\}\}/g)) matches.add(m[1]);
    return [...matches].sort((a, b) => Number(a) - Number(b));
  });

  protected readonly rendered = computed(() => {
    const body = this.bodyTextOf(this.selected()) ?? '';
    const values = this.varValues();
    return body.replace(/\{\{(\d+)\}\}/g, (_, n: string) => values[n] ?? `{{${n}}}`);
  });

  /** Body split into ordered text runs and `{{n}}` variable slots for inline editing. */
  protected readonly segments = computed<readonly TemplateSegment[]>(() => {
    const body = this.bodyTextOf(this.selected());
    if (!body) return [];
    const out: TemplateSegment[] = [];
    let last = 0;
    for (const m of body.matchAll(/\{\{(\d+)\}\}/g)) {
      const idx = m.index ?? 0;
      if (idx > last) out.push({ kind: 'text', text: body.slice(last, idx) });
      out.push({ kind: 'var', n: m[1] });
      last = idx + m[0].length;
    }
    if (last < body.length) out.push({ kind: 'text', text: body.slice(last) });
    return out;
  });

  protected readonly canSend = computed(() => {
    if (!this.selected()) return false;
    const values = this.varValues();
    return this.placeholders().every((n) => (values[n] ?? '').trim().length > 0);
  });

  protected bodyOf(t: ProviderMessageTemplate): string {
    return this.bodyTextOf(t) ?? '';
  }

  protected pick(t: ProviderMessageTemplate): void {
    this.selected.set(t);
    this.varValues.set({});
  }

  protected setVar(name: string, value: string): void {
    this.varValues.update((current) => ({ ...current, [name]: value }));
  }

  protected onClose(): void {
    this.dismissed.emit();
  }

  protected onSubmit(): void {
    const template = this.selected();
    if (!template || !this.canSend()) return;

    const placeholders = this.placeholders();
    const components: Array<Record<string, unknown>> = [];
    if (placeholders.length > 0) {
      const values = this.varValues();
      components.push({
        type: 'body',
        parameters: placeholders.map((n) => ({ type: 'text', text: values[n] ?? '' })),
      });
    }

    this.submitted.emit({
      templateId: template.id,
      templateLanguage: template.language,
      templateComponents: components,
      contentText: this.rendered(),
    });
  }

  private bodyTextOf(t: ProviderMessageTemplate | null): string | null {
    const raw = t?.components as unknown;
    if (!raw) return null;
    // Stored shape is a wrapped object `{ components: [...] }` (see template-sync.service),
    // but tolerate a bare array too.
    const components: TemplateComponent[] = Array.isArray(raw)
      ? (raw as TemplateComponent[])
      : Array.isArray((raw as { components?: unknown }).components)
        ? (raw as { components: TemplateComponent[] }).components
        : [];
    const body = components.find((c) => c.type?.toUpperCase() === 'BODY');
    return body?.text ?? null;
  }
}
